import { useCallback, useMemo, useState } from 'react';

import { ToastContext, type ToastAction, type ToastInput } from './toast-context';

/**
 * Toast provider (SCRUM-207), mounted at the AppShell level since SCRUM-322 so
 * every page can toast. Matches Ryan's bottom-right toast: brand left-border,
 * optional success variant (green border + check icon), auto-dismiss after
 * 4.5s.
 *
 * SCRUM-339 adds the v6 titled form: an optional bold title line above the
 * body ("Referral sent" / routing sentence). The original single-string
 * callers (Apply Insight, CSV export, Save Draft) render unchanged.
 *
 * SCRUM-794 adds an optional `action` (e.g. Retry). A toast carrying an action
 * is STICKY — it skips the auto-dismiss timer so the user has time to act — and
 * renders the action button plus an explicit dismiss (×). Action-less toasts are
 * unchanged (4.5s auto-dismiss), so all existing callers behave identically.
 */
interface Toast {
  id: number;
  title?: string;
  body: string;
  variant: 'default' | 'success';
  action?: ToastAction;
}

const DISMISS_MS = 4500;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string | ToastInput, variant: 'default' | 'success' = 'default') => {
      const id = Date.now() + Math.random();
      const toast: Toast =
        typeof message === 'string'
          ? { id, body: message, variant }
          : {
              id,
              body: message.body,
              variant: message.variant ?? variant,
              ...(message.title !== undefined ? { title: message.title } : {}),
              ...(message.action !== undefined ? { action: message.action } : {}),
            };
      setToasts((prev) => [...prev, toast]);
      // SCRUM-794: only action-less toasts auto-dismiss; an actionable toast stays until the
      // user clicks its action or the dismiss (×), so a Retry affordance can't vanish mid-read.
      if (!toast.action) {
        setTimeout(() => {
          dismiss(id);
        }, DISMISS_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-6 bottom-6 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-bg-4 text-text-0 flex max-w-[360px] min-w-64 items-start gap-3 rounded-md border-l-2 px-4 py-3 text-sm shadow-lg"
            style={{
              borderLeftColor:
                t.variant === 'success' ? 'var(--color-status-success)' : 'var(--cyan)',
            }}
          >
            {t.variant === 'success' && t.title && (
              <svg
                viewBox="0 0 16 16"
                width="15"
                height="15"
                fill="none"
                stroke="var(--color-status-success)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="mt-0.5 flex-none"
              >
                <path d="M2.5 8.5l3.5 3.5 7.5-8" />
              </svg>
            )}
            <div className="min-w-0 flex-1">
              {t.title && (
                <div
                  className="text-[0.8125rem] font-semibold"
                  style={
                    t.variant === 'success' ? { color: 'var(--color-status-success)' } : undefined
                  }
                >
                  {t.title}
                </div>
              )}
              <div className={t.title ? 'text-text-2 mt-0.5 text-xs leading-[1.45]' : undefined}>
                {t.body}
              </div>
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    // Fire the caller's action, then clear this toast. On repeat failure the
                    // caller shows a fresh actionable toast, so the stale one must go (SCRUM-794).
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                  className="text-brand-primary mt-2 text-xs font-semibold hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            {t.action && (
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => {
                  dismiss(t.id);
                }}
                className="text-text-3 hover:text-text-1 -mt-0.5 flex-none text-base leading-none"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
